<template>
  <v-list-item v-bind="$attrs" tag="label" link>
    <template #prepend>
      <v-icon v-if="movable" icon="drag_indicator" class="handle mr-1" />

      <v-avatar v-if="thumbnail" variant="elevated" rounded :image="thumbnail" :style="`transform: rotate(${fileData.angle}deg)`" />
      <v-avatar v-else variant="tonal" :color="extensionColor(fileData.extension)" rounded>
        <v-img v-if="getFileIconSrc(fileData)" :src="getFileIconSrc(fileData)" width="48" height="48" />
        <span v-else class="text-body-medium">{{ fileData.extension.toUpperCase() }}</span>
      </v-avatar>
    </template>

    <template #title>
      <div class="d-flex align-baseline text-body-medium" style="gap: 8px">
        <v-tooltip v-if="primary" location="top">
          <template #activator="{ props: tooltipProps }"
            ><v-icon size="14px" v-bind="tooltipProps" icon="star" color="yellow-darken-2"
          /></template>
          <template #default>{{ $t("primaryImage") }}</template>
        </v-tooltip>

        <v-tooltip v-if="fileData.general" location="top">
          <template #activator="{ props: tooltipProps }"><v-icon size="14px" v-bind="tooltipProps" icon="attach_file" /></template>
          <template #default>{{ $t("linkedFile") }}</template>
        </v-tooltip>

        <v-tooltip v-if="fileData.private" location="top">
          <template #activator="{ props: tooltipProps }"><v-icon size="14px" v-bind="tooltipProps" icon="lock" /></template>
          <template #default>{{ $t("foreclosed") }}</template>
        </v-tooltip>

        <span :title="fileData.description" class="ellipsis">{{ fileData.description }}</span>
      </div>
    </template>

    <template v-if="!hideSubtitle" #subtitle>
      <div class="d-flex text-no-wrap text-body-small ga-2" style="line-height: 1rem">
        <div>{{ $d(new Date(fileData.createdAt!)) }}</div>

        <v-divider vertical />
        <div>{{ prettyBytes(fileData.size ?? 0) }}</div>

        <template v-if="fileData.uploadedBy && !fileData.general && $vuetify.display.smAndUp">
          <v-divider vertical />
          <div>{{ fileData.uploadedBy.user.description }}</div>
          <v-divider vertical />
          <div>{{ fileData.uploadedBy.organization.description }}</div>
        </template>

        <template v-if="fileData.remark">
          <v-divider vertical />
          <div>{{ fileData.remark }}</div>
        </template>
      </div>
    </template>

    <template #append>
      <v-card-actions class="pa-0 d-flex" style="min-height: 40px">
        <slot name="actions" />

        <v-tooltip v-if="onFullscreen" :text="$t('fullscreen')" location="top">
          <template #activator="{ props: tooltipProps }">
            <v-btn v-bind="tooltipProps" icon="fullscreen" variant="text" size="small" @click.stop="onFullscreen(fileData._id)" />
          </template>
        </v-tooltip>

        <v-btn
          v-if="!isSuperAdmin && (fileData.general || (fileData.private && !isOrganizationAdmin))"
          :id="`action-${fileData._id}`"
          icon="visibility"
          target="_blank"
          :href="`/files/${fileData._id}`"
        />

        <file-actions
          v-else-if="!hideFileActions"
          v-model="fileData"
          style="order: 1"
          :hide-delete="hideDelete"
          :hide-download="hideDownload"
          :hide-rename="hideRename"
          :hide-remark="hideRemark"
          :hide-rotate="hideRotate"
          :hide-visibility="hideVisibility"
          @delete="() => emit('delete')"
        />
      </v-card-actions>
    </template>
  </v-list-item>
</template>

<script setup lang="ts">
import prettyBytes from "pretty-bytes"

const fileStore = useFileStore()
const authStore = useAuthStore()
const { getFileIconSrc } = useFileExtensions()
const { isSuperAdmin, currentOrganization } = storeToRefs(authStore)

const isOrganizationAdmin = authStore.hasScope(currentOrganization.value!, AuthScope.ADMIN)

const fileData = defineModel<FileData>({ required: true })

const emit = defineEmits<{
  (e: "delete"): void
}>()

const props = defineProps<{
  movable?: boolean
  hideDownload?: boolean
  hideDelete?: boolean
  hideRename?: boolean
  hideRemark?: boolean
  hideRotate?: boolean
  hideVisibility?: boolean
  hideSubtitle?: boolean
  hideFileActions?: boolean
  onFullscreen?: (fileId: string) => void
  primary?: boolean
}>()
const { movable, hideDownload, hideDelete, hideRename, hideRemark, hideRotate, hideVisibility, hideSubtitle, hideFileActions, primary } =
  toRefs(props)

const thumbnail = ref<string>()

watch(
  fileData,
  async () => {
    if (fileData.value.type === "IMAGE" && thumbnail.value === undefined) {
      thumbnail.value = await fileStore.getFileUrl(fileData.value._id, "/no-image.png", true)
    }
  },
  { immediate: true, deep: true },
)
</script>
